/**
 * Multi-patient central monitoring wall (Epic C4).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
} from "react-bootstrap";
import apiClient from "@shared/services/apiClient";
import { useAuth } from "@shared/context/AuthContext";
import {
  CENTRAL_MONITORING_WALL_SLAS,
  WALL_ALARM_FATIGUE_CONTROLS,
  createMonitoringWall,
  estimateWallLoadEvidence,
  exportWallAlertAudit,
  type CentralMonitoringWall,
  type WallAlert,
  type WallLayoutSize,
  type WallPatientTile,
} from "../utils/centralMonitoringWall";
import {
  acknowledgeMonitoringAlertApi,
  createMonitoringAlertApi,
  listMonitoringAlertsApi,
  mapMonitoringAlertToWallAlert,
} from "../services/monitoringAlertsApi";

type WindowWithPlacement = Window & {
  getScreenDetails?: () => Promise<{
    currentScreen?: { left: number; top: number; width: number; height: number };
    screens?: Array<{ left: number; top: number; width: number; height: number }>;
  }>;
};

type WorklistItem = {
  eeg_study_id?: string;
  recording_id?: string | null;
  patient_id?: string | null;
  patient_name?: string | null;
  patient_mrn?: string | null;
  last_reviewed_at?: string | null;
  pending_event_count?: number;
  significant_change?: boolean;
  status?: string | null;
  priority?: string | null;
  study_type?: string | null;
  modality?: string | null;
  monitoring_type?: string | null;
  service_line?: string | null;
  location?: string | null;
  unit?: string | null;
};

type MonitoringWallPatient = {
  studyId: string;
  patientId: string | null;
  patientLabel: string;
  status: WallPatientTile["status"];
  lastSampleAgeMs: number;
  pendingAlerts: number;
  recordingId: string | null;
};

const STATUS_LABELS: Record<WallPatientTile["status"], string> = {
  ok: "Stable",
  warning: "Review",
  critical: "Critical",
  offline: "Offline",
};

function isLiveMonitoringWorklistItem(item: WorklistItem): boolean {
  const searchable = [
    item.study_type,
    item.modality,
    item.monitoring_type,
    item.service_line,
    item.location,
    item.unit,
    item.priority,
    item.status,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/[_-]/g, " ")
    .toLowerCase();
  return /\b(icu|ceeg|c eeg|continuous|critical|neonatal|ltm|monitoring|active)\b/.test(
    searchable,
  );
}

function toWallPatient(item: WorklistItem): MonitoringWallPatient | null {
  if (!item.eeg_study_id) return null;
  const patientLabel =
    [item.patient_mrn, item.patient_name].filter(Boolean).join(" / ") ||
    item.eeg_study_id;
  const priority = (item.priority || "").toLowerCase();
  const status = (item.status || "").toLowerCase();
  const lastReviewedAt = item.last_reviewed_at
    ? Date.parse(item.last_reviewed_at)
    : null;
  const lastSampleAgeMs =
    lastReviewedAt && Number.isFinite(lastReviewedAt)
      ? Math.max(0, Date.now() - lastReviewedAt)
      : CENTRAL_MONITORING_WALL_SLAS.tileStatusRefreshMs;
  return {
    studyId: item.eeg_study_id,
    patientId: item.patient_id ?? null,
    patientLabel,
    status:
      priority.includes("critical") || status.includes("critical")
        ? "critical"
        : priority.includes("stat") ||
            priority.includes("urgent") ||
            status.includes("pending") ||
            item.significant_change
          ? "warning"
          : "ok",
    lastSampleAgeMs,
    pendingAlerts: item.pending_event_count ?? 0,
    recordingId: item.recording_id ?? null,
  };
}

function formatSampleAge(ageMs: number): string {
  if (ageMs < 10_000) return "just now";
  const minutes = Math.round(ageMs / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.round(minutes / 60)} h ago`;
}

export default function CentralMonitoringWallPanel() {
  const { user } = useAuth();
  const wallRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<WallLayoutSize>(8);
  const [patients, setPatients] = useState<MonitoringWallPatient[]>([]);
  const [wall, setWall] = useState<CentralMonitoringWall>(() =>
    createMonitoringWall({ patients: [], layout: 8 }),
  );
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [persisting, setPersisting] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [lastReviewOpened, setLastReviewOpened] = useState<string | null>(null);

  const [wallAudit, setWallAudit] = useState<
    CentralMonitoringWall["audit"]
  >([]);

  const applyWallState = useCallback(
    (
      nextPatients: MonitoringWallPatient[],
      nextAlerts: WallAlert[],
      nextLayout: WallLayoutSize,
      nextAudit: CentralMonitoringWall["audit"] = [],
    ) => {
      const base = createMonitoringWall({
        patients: nextPatients,
        layout: nextLayout,
      });
      const openByStudy = nextAlerts.reduce<Record<string, number>>((acc, alert) => {
        if (!alert.acknowledged) {
          acc[alert.studyId] = (acc[alert.studyId] || 0) + 1;
        }
        return acc;
      }, {});
      setWallAudit(nextAudit);
      setWall({
        ...base,
        alerts: nextAlerts,
        audit: nextAudit,
        tiles: base.tiles.map((tile) => ({
          ...tile,
          pendingAlerts: openByStudy[tile.studyId] ?? tile.pendingAlerts,
          status:
            (openByStudy[tile.studyId] || 0) > 0 && tile.status === "ok"
              ? "warning"
              : tile.status,
        })),
      });
    },
    [],
  );

  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/dashboard/worklist");
      const nextPatients: MonitoringWallPatient[] = (
        (response.data?.data ?? []) as WorklistItem[]
      )
        .filter(isLiveMonitoringWorklistItem)
        .map(toWallPatient)
        .filter((patient): patient is MonitoringWallPatient => Boolean(patient));
      setPatients(nextPatients);
      setSelectedStudyId((current) =>
        current && nextPatients.some((patient) => patient.studyId === current)
          ? current
          : nextPatients[0]?.studyId || "",
      );

      let nextAlerts: WallAlert[] = [];
      if (nextPatients.length > 0) {
        try {
          const alerts = await listMonitoringAlertsApi({
            studyIds: nextPatients.map((patient) => patient.studyId),
            hours: 24,
            limit: 200,
          });
          nextAlerts = alerts.map(mapMonitoringAlertToWallAlert);
        } catch {
          nextAlerts = [];
        }
      }
      applyWallState(nextPatients, nextAlerts, layout, []);
      setLoadError(null);
      setActionError(null);
    } catch (error: any) {
      setPatients([]);
      setSelectedStudyId("");
      applyWallState([], [], layout, []);
      setLoadError(
        error?.response?.data?.error ||
          "Unable to load live monitoring studies from the clinical worklist.",
      );
    } finally {
      setLoading(false);
    }
  }, [applyWallState, layout]);

  useEffect(() => {
    void loadPatients();
  }, [loadPatients]);

  useEffect(() => {
    const syncFullscreenState = () => {
      setFullScreen(document.fullscreenElement === wallRef.current);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const load = useMemo(
    () =>
      estimateWallLoadEvidence({
        concurrentPatients: wall.tiles.length,
        alertRatePerMin: wall.alerts.filter((a) => !a.acknowledged).length * 2,
      }),
    [wall],
  );

  const setLayoutSize = (size: WallLayoutSize) => {
    setLayout(size);
    applyWallState(patients, wall.alerts, size, wallAudit);
  };

  const raiseCriticalAlert = async () => {
    const studyId = selectedStudyId || wall.tiles[0]?.studyId;
    const patient = patients.find((item) => item.studyId === studyId);
    if (!studyId || !patient?.patientId || !patient.recordingId || persisting) {
      setActionError(
        "Selected study needs patient_id and recording_id from the worklist before an alert can be persisted.",
      );
      return;
    }

    setPersisting(true);
    setActionError(null);
    try {
      const created = await createMonitoringAlertApi({
        studyId,
        patientId: patient.patientId,
        recordingId: patient.recordingId,
        alertType: "critical_pattern",
        severity: "critical",
        message: "Critical EEG pattern requires reviewer acknowledgement",
        metadata: {
          user_id: user?.id,
          source: "central_monitoring_wall",
        },
      });
      const wallAlert = mapMonitoringAlertToWallAlert(created);
      const atIso = new Date().toISOString();
      applyWallState(
        patients,
        [...wall.alerts, wallAlert],
        layout,
        [
          ...wallAudit,
          {
            atIso,
            actorId: user?.id || "system",
            action: "raise",
            alertId: wallAlert.alertId,
            studyId,
          },
        ],
      );
    } catch (error: any) {
      setActionError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to persist monitoring alert.",
      );
    } finally {
      setPersisting(false);
    }
  };

  const ackFirst = async () => {
    const open = wall.alerts.find((a) => !a.acknowledged);
    if (!open || persisting) return;

    setPersisting(true);
    setActionError(null);
    try {
      await acknowledgeMonitoringAlertApi(open.alertId);
      const atIso = new Date().toISOString();
      const actorId = user?.id || "system";
      applyWallState(
        patients,
        wall.alerts.map((alert) =>
          alert.alertId === open.alertId
            ? {
                ...alert,
                acknowledged: true,
                acknowledgedAtIso: atIso,
                acknowledgedBy: actorId,
              }
            : alert,
        ),
        layout,
        [
          ...wallAudit,
          {
            atIso,
            actorId,
            action: "ack",
            alertId: open.alertId,
            studyId: open.studyId,
          },
        ],
      );
    } catch (error: any) {
      setActionError(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to acknowledge monitoring alert.",
      );
    } finally {
      setPersisting(false);
    }
  };

  const toggleFullScreen = async () => {
    if (document.fullscreenElement === wallRef.current) {
      await document.exitFullscreen?.();
      setFullScreen(false);
      return;
    }
    try {
      await wallRef.current?.requestFullscreen?.();
      setFullScreen(true);
    } catch {
      setFullScreen((value) => !value);
    }
  };

  const openReviewWindow = async (patient: MonitoringWallPatient) => {
    const reviewPath = patient.recordingId
      ? `/eeg/${patient.studyId}?recordingId=${patient.recordingId}`
      : `/monitoring/icu/${encodeURIComponent(patient.studyId)}`;
    const reviewUrl = new URL(reviewPath, window.location.origin).toString();
    const popupFeatures = [
      "popup=yes",
      "noopener=yes",
      "noreferrer=yes",
      "width=1440",
      "height=1000",
    ];

    try {
      const placementWindow = window as WindowWithPlacement;
      const details = await placementWindow.getScreenDetails?.();
      const current = details?.currentScreen;
      const nextScreen = details?.screens?.find(
        (screen) =>
          !current ||
          screen.left !== current.left ||
          screen.top !== current.top ||
          screen.width !== current.width ||
          screen.height !== current.height,
      );
      if (nextScreen) {
        popupFeatures.push(
          `left=${nextScreen.left}`,
          `top=${nextScreen.top}`,
          `width=${nextScreen.width}`,
          `height=${nextScreen.height}`,
        );
      }
    } catch {
      // Browsers may require permission or lack Window Management API support.
    }

    window.open(reviewUrl, "_blank", popupFeatures.join(","));
    setLastReviewOpened(patient.patientLabel);
  };

  const audit = exportWallAlertAudit(wall);
  const selectedPatient = patients.find(
    (patient) => patient.studyId === selectedStudyId,
  );

  return (
    <Container
      ref={wallRef}
      fluid
      className={`py-3 app-ui-page-fit${
        fullScreen ? " monitoring-wall-fullscreen" : ""
      }`}
      data-testid="central-monitoring-wall"
      style={
        fullScreen
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 2050,
              overflow: "auto",
              background: "var(--color-background)",
              padding: "1rem",
            }
          : undefined
      }
    >
      <Row className="mb-3">
        <Col>
          <h1 className="h4 mb-1">Central monitoring wall</h1>
          <div className="text-muted small">
            Live ICU/cEEG studies from the clinical worklist, with visual status,
            reviewer acknowledgement, and status-only degradation when wall
            performance misses the monitoring SLA.
          </div>
        </Col>
        <Col xs="auto">
          <div className="d-flex flex-wrap gap-2 justify-content-end">
            <Button
              size="sm"
              variant={fullScreen ? "secondary" : "outline-secondary"}
              onClick={() => void toggleFullScreen()}
              aria-pressed={fullScreen}
            >
              {fullScreen ? "Exit full screen" : "Full screen wall"}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => void loadPatients()}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </Col>
      </Row>

      <Alert variant="secondary" className="mb-3">
        Wall load target: p95 under{" "}
        {CENTRAL_MONITORING_WALL_SLAS.mosaicLoadP95Ms / 1000}s. Critical alerts
        require acknowledgement; non-critical sounds are suppressed during alert
        storms. This wall is review support only and does not control therapy.
      </Alert>

      {loadError && <Alert variant="warning">{loadError}</Alert>}
      {actionError && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      {!loading && !loadError && wall.tiles.length === 0 && (
        <Alert variant="info">
          No active ICU/cEEG monitoring studies were returned from the clinical
          worklist. This page no longer creates demo bed tiles.
        </Alert>
      )}

      <div className="d-flex flex-wrap gap-2 mb-3">
        {CENTRAL_MONITORING_WALL_SLAS.layouts.map((size) => (
          <Button
            key={size}
            size="sm"
            variant={layout === size ? "primary" : "outline-primary"}
            onClick={() => setLayoutSize(size)}
            disabled={loading}
          >
            {size}-up
          </Button>
        ))}
        <Form.Select
          size="sm"
          aria-label="Selected monitored study"
          value={selectedStudyId}
          onChange={(event) => setSelectedStudyId(event.target.value)}
          disabled={wall.tiles.length === 0}
          className="w-auto"
        >
          {patients.map((patient) => (
            <option key={patient.studyId} value={patient.studyId}>
              {patient.patientLabel}
            </option>
          ))}
        </Form.Select>
        <Button
          size="sm"
          variant="warning"
          onClick={() => void raiseCriticalAlert()}
          data-testid="wall-raise-alert"
          disabled={!selectedStudyId || persisting}
        >
          Flag critical alert
        </Button>
        <Button
          size="sm"
          variant="success"
          onClick={() => void ackFirst()}
          data-testid="wall-ack-alert"
          disabled={
            persisting || !wall.alerts.some((alert) => !alert.acknowledged)
          }
        >
          Acknowledge first open alert
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() =>
            selectedPatient ? void openReviewWindow(selectedPatient) : undefined
          }
          disabled={!selectedPatient}
        >
          Open review in new window
        </Button>
      </div>

      {lastReviewOpened && (
        <Alert variant="light" className="border py-2">
          Review opened for {lastReviewOpened}. Alert acknowledgement remains
          separate.
        </Alert>
      )}

      {wall.degradedMode && wall.tiles.length > 0 ? (
        <Alert variant="warning">
          The wall is in status-only mode because live tiles are stale.
        </Alert>
      ) : null}

      <Row className="g-2 mb-3" data-testid="wall-mosaic">
        {wall.tiles.map((tile) => {
          const patient = patients.find((item) => item.studyId === tile.studyId);
          return (
            <Col
              key={tile.tileId}
              xs={12}
              sm={6}
              md={layout >= 12 ? 3 : 4}
              lg={layout >= 16 ? 3 : 3}
            >
              <Card
                border={
                  tile.status === "critical"
                    ? "danger"
                    : tile.status === "warning"
                      ? "warning"
                      : "success"
                }
              >
                <Card.Body className="py-2">
                  <div className="d-flex justify-content-between">
                    <strong>{tile.patientLabel}</strong>
                    <Badge
                      bg={
                        tile.status === "critical"
                          ? "danger"
                          : tile.status === "warning"
                            ? "warning"
                            : "success"
                      }
                    >
                      {STATUS_LABELS[tile.status]}
                    </Badge>
                  </div>
                  <small className="text-muted">
                    Last worklist update {formatSampleAge(tile.lastSampleAgeMs)}
                    {tile.pendingAlerts > 0
                      ? ` · ${tile.pendingAlerts} pending event${
                          tile.pendingAlerts === 1 ? "" : "s"
                        }`
                      : ""}
                  </small>
                  <div>
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0"
                      onClick={() =>
                        patient ? void openReviewWindow(patient) : undefined
                      }
                      data-testid={`wall-jump-${tile.studyId}`}
                    >
                      {patient?.recordingId
                        ? "Open raw + qEEG review"
                        : "Open monitoring details"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Row className="g-3">
        <Col md={6}>
          <Card>
            <Card.Header>Alerts</Card.Header>
            <Card.Body data-testid="wall-alerts">
              {wall.alerts.length === 0 ? (
                <span className="text-muted">No alerts</span>
              ) : (
                wall.alerts.map((alert) => (
                  <div key={alert.alertId} className="mb-2">
                    <Badge
                      bg={alert.acknowledged ? "secondary" : "danger"}
                      className="me-2"
                    >
                      {alert.severity === "critical"
                        ? "Critical"
                        : alert.severity}
                    </Badge>
                    {alert.message}
                    {alert.audible ? <small> Audio enabled</small> : null}
                    {alert.acknowledged ? (
                      <small className="text-muted">
                        {" "}
                        Acknowledged by {alert.acknowledgedBy}
                      </small>
                    ) : null}
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>Performance and audit</Card.Header>
            <Card.Body className="small" data-testid="wall-load-evidence">
              <div>
                Estimated wall load: {load.estimatedMosaicLoadMs} ms. Alert
                delivery estimate: {load.estimatedFanoutMs} ms.{" "}
                {load.withinSla
                  ? "Current estimates are within the monitoring SLA."
                  : "Current estimates exceed the monitoring SLA."}
              </div>
              <div className="mt-2">
                If the SLA is missed, the wall falls back to status-only tiles,
                mutes non-critical audio, and keeps critical acknowledgement
                required.
              </div>
              <div className="mt-2" data-testid="wall-audit">
                Acknowledgement audit entries: {audit.length}
              </div>
              <div className="mt-2 text-muted">
                Alarm fatigue control: {WALL_ALARM_FATIGUE_CONTROLS.riskId}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
